import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import { Loader, Icon } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { formatApiError } from '../../utils/apiUtils';
import { fetchObjectTypeCounts } from '../../redux/migrationThunks';
import { ERROR_MESSAGES, TITLES, UI_LABELS } from '../../constants/stringConstants';
import {
  SAILPOINT_TILES_ORDER,
  SAILPOINT_OBJECT_TYPE_TO_TILE_NAME,
  SAILPOINT_EXCLUDED_TYPES,
  OIM_TILES_ORDER,
} from '../../constants/migrationConstants';
import VisibilityCard from './VisibilityCard';
import useStyles from './styles/index.styles';

function Visibility({ sourceType, sourceID }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [visibilityData, setVisibilityData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sourceID) return;
    const fetchVisibilityData = async () => {
      try {
        setIsLoading(true);

        // Single API call for both source types
        const apiData = await dispatch(
          fetchObjectTypeCounts({
            sourceId: sourceID,
            sourceType,
          })
        ).unwrap();

        let data;
        if (sourceType?.includes('oim')) {
          const orderIndex = Object.fromEntries(
            OIM_TILES_ORDER.map((type, index) => [type, index])
          );
          data = [...(apiData?.data || [])].sort((a, b) => {
            const aIndex = orderIndex[a.objectType];
            const bIndex = orderIndex[b.objectType];
            if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
            if (aIndex !== undefined) return -1;
            if (bIndex !== undefined) return 1;
            return 0;
          });
        } else {
          const orderMap = SAILPOINT_TILES_ORDER.reduce((acc, name, index) => {
            acc[name] = index;
            return acc;
          }, {});
          data = apiData?.data
            .filter((item) => !SAILPOINT_EXCLUDED_TYPES.has(item.objectType))
            .sort((a, b) => {
              const aTile = SAILPOINT_OBJECT_TYPE_TO_TILE_NAME[a.objectType];
              const bTile = SAILPOINT_OBJECT_TYPE_TO_TILE_NAME[b.objectType];
              const aOrder = orderMap[aTile] ?? Number.MAX_SAFE_INTEGER;
              const bOrder = orderMap[bTile] ?? Number.MAX_SAFE_INTEGER;
              return aOrder - bOrder;
            });
        }

        setVisibilityData(data);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchVisibilityData();
  }, [sourceID, sourceType, dispatch]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  if (error || !visibilityData || (visibilityData.length === 0 && !sourceType?.includes('oim'))) {
    return (
      <div className={classes.errorContainer}>
        <div className={classes.errorIconWrap}>
          <Icon kind="AlertCriticalOutline" color="--color-critical-700" label="Error" size="large" />
        </div>
        <div className={classes.errorText}>
          <Typography className={classes.errorTitle}>
            {ERROR_MESSAGES.BACKEND_UNAVAILABLE}
          </Typography>
          <Typography className={classes.errorMessage}>
            {ERROR_MESSAGES.CANNOT_CONNECT_BACKEND}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          {TITLES.OBJECT_VISIBILITY}
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          {UI_LABELS.OVERVIEW_DISCOVERED}{' '}
          {sourceType?.includes('sailpoint') ? UI_LABELS.SAILPOINT : UI_LABELS.OIM}{' '}
          {UI_LABELS.ENVIRONMENT}
        </Typography>
      </div>

      <div className={classes.grid}>
        {visibilityData.map((item) => (
          <VisibilityCard key={item.objectType} item={item} sourceType={sourceType} />
        ))}
      </div>
    </div>
  );
}

Visibility.propTypes = {
  sourceType: PropTypes.string.isRequired,
  sourceID: PropTypes.string,
};

Visibility.defaultProps = {
  sourceID: null,
};

export default Visibility;
